// src/components/edges/CustomEdge.tsx
import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { getSmoothStepPath, BaseEdge, EdgeProps, Position, useStore as useReactFlowStore, EdgeLabelRenderer } from '@xyflow/react';
import { DeleteButton } from '../DeleteButton';
import { useStore } from '../../store/store';
import useTabManagerStore from '../../store/tabManagerStore';
import { CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { EdgeData } from '../../store/store';
import { deleteEdgeWithLogging } from '../../utils/deleteUtils';
import { getEdgeParams } from '../../utils/edgeUtils';
import { createContextLogger } from '../../utils/logger';

const logger = createContextLogger('CustomEdge');

// Type definitions
interface CustomEdgeProps extends EdgeProps {
  data?: EdgeData;
}

type EdgeCondition = 'true' | 'false' | 'unconditional' | 'info' | 'error_handling';

type IconComponent = React.ComponentType<{ style?: React.CSSProperties }>;

// Define constants outside the component
const iconSize = 24; // The desired size of the icon viewport (increased for better visibility)
const colors: Record<EdgeCondition | 'disabled', string> = {
  true: '#28a745', // Green
  false: '#dc3545', // Red
  unconditional: '#555', // Dark Gray/Black
  disabled: '#ccc', // Light Gray
  info: '#1890ff', // Blue
  error_handling: '#dc3545', // Red (same as false)
};

// Add constants at the top with other constants
const deleteButtonSize = 24; // Increased from 20
const deleteButtonOffset = 15; // Distance from edge

// Custom double vertical bars icon component for parallel edges
const DoubleVerticalBarsIcon: React.FC<{ style?: React.CSSProperties }> = ({ style }) => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center',
    width: '100%',
    height: '100%',
    ...style 
  }}>
    <div style={{
      display: 'flex',
      gap: '2px',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: '2px',
        height: '12px',
        backgroundColor: style?.color || '#555',
        borderRadius: '1px',
      }} />
      <div style={{
        width: '2px',
        height: '12px',
        backgroundColor: style?.color || '#555',
        borderRadius: '1px',
      }} />
    </div>
  </div>
);

const CustomEdge: React.FC<CustomEdgeProps> = ({
  id,
  source,
  target,
  style = {},
  markerEnd,
  data,
  selected,
}) => {
  const [showActions, setShowActions] = useState<boolean>(false);
  const hideTimer = useRef<number | null>(null);
  const deleteEdge = useStore((state) => state.deleteEdge);
  const updatePipeline = useStore((state) => state.updatePipeline);
  const activePipelineId = useStore((state) => state.activePipelineId);
  const edges = useStore((state) => {
    const pipeline = state.pipelines.find(p => p.id === state.activePipelineId);
    return pipeline?.edges || [];
  });
  const markStructuralChange = useStore((state) => state.markStructuralChange);
  const saveToHistory = useStore((state) => state.saveToHistory);
  
  // Get updateTabModified from tab manager store
  const updateTabModified = useTabManagerStore((state) => state.updateTabModified);

  // Get source and target nodes for floating edge calculation using React Flow's store
  const sourceNode = useReactFlowStore(useCallback((store) => store.nodeLookup.get(source), [source]));
  const targetNode = useReactFlowStore(useCallback((store) => store.nodeLookup.get(target), [target]));

  // Check if source node has parallel=true
  const isSourceParallel = sourceNode?.data?.parallel === true;
  
  // Return null if nodes aren't available yet
  if (!sourceNode || !targetNode) {
    return null;
  }

  // Calculate floating edge parameters
  const { sx, sy, tx, ty, sourcePos, targetPos } = getEdgeParams(sourceNode, targetNode);

  // Helper function to find all downstream edges recursively
  const findDownstreamEdges = useCallback((edgeId: string, visited: Set<string> = new Set()): string[] => {
    // Prevent infinite loops
    if (visited.has(edgeId)) {
      return [];
    }
    visited.add(edgeId);
    
    const edge = edges.find(e => e.id === edgeId);
    if (!edge) {
      logger.debug('Edge not found:', edgeId);
      return [];
    }
    
    logger.debug('Finding downstream from edge:', {
      edgeId,
      source: edge.source,
      target: edge.target,
    });
    
    const targetNode = edge.target;
    const downstreamEdges = edges.filter(e => e.source === targetNode);
    
    logger.debug('Found downstream edges:', {
      targetNode,
      downstreamCount: downstreamEdges.length,
      downstreamIds: downstreamEdges.map(e => e.id),
    });
    
    const allDownstream: string[] = [];
    
    downstreamEdges.forEach(downEdge => {
      allDownstream.push(downEdge.id);
      // Recursively find edges downstream of this edge
      const recursive = findDownstreamEdges(downEdge.id, visited);
      allDownstream.push(...recursive);
    });
    
    return allDownstream;
  }, [edges]);

  // Update handlers for enable/disable
  const handleEnableDownstream = useCallback((): void => {
    if (!activePipelineId) return;
    
    logger.info('🔄 Starting enable downstream for edge:', id);
    logger.debug('Current edges:', edges.map(e => ({ id: e.id, source: e.source, target: e.target })));
    
    const downstreamEdgeIds = findDownstreamEdges(id);
    const edgesToUpdate = [id, ...downstreamEdgeIds];
    
    logger.info('📋 Edges to update:', {
      currentEdge: id,
      downstreamEdges: downstreamEdgeIds,
      totalCount: edgesToUpdate.length,
    });
    
    // Save to history
    if (saveToHistory) {
      saveToHistory(`Enable downstream hops`);
    }
    
    // Update all edges
    const updatedEdges = edges.map(e => 
      edgesToUpdate.includes(e.id)
        ? { 
            ...e, 
            data: { 
              ...e.data, 
              enabled: true,
              condition: e.data?.condition || 'unconditional',
              fields: e.data?.fields ?? []
            } 
          }
        : e
    );
    
    logger.info('📝 Updated edges:', updatedEdges.filter(e => edgesToUpdate.includes(e.id)).map(e => ({
      id: e.id,
      enabled: e.data?.enabled,
    })));
    
    updatePipeline(activePipelineId, { edges: updatedEdges });
    
    // Mark tab as modified
    if (updateTabModified) {
      updateTabModified(activePipelineId, true);
    }
    
    logger.info('✅ Enabled downstream hops complete');
  }, [id, edges, activePipelineId, findDownstreamEdges, updatePipeline, updateTabModified, saveToHistory]);

  const handleDisableDownstream = useCallback((): void => {
    if (!activePipelineId) return;
    
    const downstreamEdgeIds = findDownstreamEdges(id);
    const edgesToUpdate = [id, ...downstreamEdgeIds];
    
    // Save to history
    if (saveToHistory) {
      saveToHistory(`Disable downstream hops`);
    }
    
    // Update all edges
    const updatedEdges = edges.map(e => 
      edgesToUpdate.includes(e.id)
        ? { 
            ...e, 
            data: { 
              ...e.data, 
              enabled: false,
              condition: e.data?.condition || 'unconditional',
              fields: e.data?.fields ?? []
            } 
          }
        : e
    );
    
    updatePipeline(activePipelineId, { edges: updatedEdges });
    
    // Mark tab as modified
    if (updateTabModified) {
      updateTabModified(activePipelineId, true);
    }
    
    logger.info('⏸️ Disabled downstream hops:', {
      edgeId: id,
      downstreamCount: downstreamEdgeIds.length,
    });
  }, [id, edges, activePipelineId, findDownstreamEdges, updatePipeline, updateTabModified, saveToHistory]);

  // Handler for swapping edge direction
  const handleSwapDirection = useCallback((): void => {
    if (!activePipelineId) return;
    
    // Save to history before making the change
    if (saveToHistory) {
      saveToHistory('Swap edge direction');
    }
    
    // Swap source and target
    const updatedEdges = edges.map(e => 
      e.id === id 
        ? { 
            ...e,
            id: `${target}-${source}`, // Update ID to reflect new direction
            source: target,
            target: source,
          }
        : e
    );
    
    updatePipeline(activePipelineId, { edges: updatedEdges });
    
    // Mark tab as modified
    if (updateTabModified) {
      updateTabModified(activePipelineId, true);
    }
    
    // Mark structural change
    if (markStructuralChange) {
      markStructuralChange(activePipelineId);
    }
    
    logger.info('🔄 Edge direction swapped:', {
      edgeId: id,
      oldSource: source,
      oldTarget: target,
      newSource: target,
      newTarget: source,
    });
  }, [id, source, target, edges, activePipelineId, updatePipeline, updateTabModified, markStructuralChange, saveToHistory]);

  const handleMouseEnter = useCallback((): void => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
    }
    setShowActions(true);
  }, []);

  const handleMouseLeave = useCallback((): void => {
    hideTimer.current = window.setTimeout(() => {
      setShowActions(false);
    }, 100); // Small delay to prevent flickering
  }, []);

  // Simple callback that uses the shared delete utility
  const handleDelete = useCallback((): void => {
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
    }
    
    // Use the shared delete utility for consistency
    deleteEdgeWithLogging({
      edgeId: id,
      deleteEdge,
      // Note: We don't have access to updateTabModified or pipelineId here
      // This could be improved by passing these down as props
    });
  }, [deleteEdge, id]);

  // Clean up the timer when component unmounts
  useEffect(() => {
    return () => {
      if (hideTimer.current) {
        clearTimeout(hideTimer.current);
      }
    };
  }, []);

  // Calculate edge path using SmoothStep for right-angled connections with floating positions
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX: sx,
    sourceY: sy,
    sourcePosition: sourcePos,
    targetPosition: targetPos,
    targetX: tx,
    targetY: ty,
  });

  // --- 2. Calculate Edge Style ---
  const edgeStyle = useMemo(() => {
    const baseColor = !data?.enabled ? colors.disabled : colors[data?.condition || 'unconditional'];
    
    // Determine stroke dash pattern based on conditions
    let strokeDasharray = 'none';
    if (!data?.enabled || data?.condition === 'error_handling') {
      strokeDasharray = '5, 5';
    } else if (isSourceParallel) {
      // Use dotted pattern for parallel nodes
      strokeDasharray = '2, 3';
    }
    
    return {
      ...style,
      stroke: baseColor,
      strokeWidth: 1.5,
      strokeDasharray,
    };
  }, [data?.condition, data?.enabled, style, isSourceParallel]);

  // --- 3. Calculate Marker Properties ---
  // Encode the edge ID to make it valid for SVG marker IDs (no spaces or special chars)
  const markerEndId = `arrow-${id.replace(/[^a-zA-Z0-9-_]/g, '_')}`;
  const markerProps = useMemo(
    () => ({
      type: 'arrowclosed' as const,
      color: edgeStyle?.stroke || colors.unconditional,
      width: 20,
      height: 20,
    }),
    [edgeStyle?.stroke]
  );

  // --- 4. Calculate Icon Color ---
  const iconColor = useMemo(
    () => (!data?.enabled ? colors.disabled : colors[data?.condition || 'unconditional']),
    [data?.condition, data?.enabled]
  );

  // --- 5. Determine Icon Component ---
  const IconComponent = useMemo((): IconComponent => {
    // If source is parallel, use double vertical bars icon
    if (isSourceParallel) {
      return DoubleVerticalBarsIcon;
    }
    
    // Otherwise use the normal condition-based icons
    if (!data?.enabled) return MinusCircleOutlined;
    switch (data?.condition) {
      case 'true':
        return CheckCircleOutlined;
      case 'false':
        return CloseCircleOutlined;
      default:
        return MinusCircleOutlined;
    }
  }, [data?.condition, data?.enabled, isSourceParallel]);

  // --- 6. Calculate Position for foreignObject (Top-Left Corner) ---
  // We position the top-left corner of the foreignObject so the icon appears centered
  const foreignObjectX = labelX - iconSize / 2;
  const foreignObjectY = labelY - iconSize / 2;

  // Add edge orientation calculation
  const edgeOrientation = useMemo(() => {
    const dx = Math.abs(tx - sx);
    const dy = Math.abs(ty - sy);
    return dx > dy ? 'horizontal' : 'vertical';
  }, [sx, sy, tx, ty]);

  // Add edge position relative to canvas calculation
  const isNearLeftBorder = useMemo(() => {
    const centerX = (sx + tx) / 2;
    return centerX < window.innerWidth / 2;
  }, [sx, tx]);

  // Calculate delete button position
  // This deleteButtonPosition might need adjustment for the new wider EdgeActionsContainer
  // For simplicity, let's use labelX and labelY for now for the EdgeActionsContainer
  // const deleteButtonPosition = useMemo(() => { ... }); // You might not need this specific calculation anymore for the action container

  // Define the size for the foreignObject when rendering edge actions
  const edgeActionsWidth = 110; // Increased to fit 4 icons
  const edgeActionsHeight = 32;

  // --- 7. Render Edge with EdgeLabelRenderer for labels ---
  return (
    <>
    <g onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* Define the arrow marker */}
      <defs>
        <marker
          id={markerEndId}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path
            d="M 0 0 L 10 5 L 0 10 z"
            fill={markerProps.color}
            stroke="none"
          />
        </marker>
      </defs>
      
      {/* Render the base edge path with arrow marker */}
      <BaseEdge 
        id={id} 
        path={edgePath || 'M 0 0'} 
        markerEnd={`url(#${markerEndId})`}
        markerStart={undefined}
        style={edgeStyle}
      />
    </g>
    
    {/* Use EdgeLabelRenderer for proper positioning and layering */}
    <EdgeLabelRenderer>
      {/* Render icon label - always visible if coordinates are valid */}
      {!isNaN(labelX) && !isNaN(labelY) && (
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: `${iconSize}px`,
            height: `${iconSize}px`,
            background: 'white',
            borderRadius: '50%',
            border: '1px solid #d9d9d9',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            zIndex: 10,
          }}
          className="nodrag nopan"
        >
          <IconComponent
            style={{
              fontSize: iconSize - 4,
              color: iconColor,
            }}
          />
        </div>
      )}
      
      {/* Render action buttons when hovering */}
      {showActions && !isNaN(labelX) && !isNaN(labelY) && (
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY - 35}px)`,
            pointerEvents: 'all',
            width: `${edgeActionsWidth}px`,
            height: `${edgeActionsHeight}px`,
            zIndex: 1000,
          }}
          className="nodrag nopan"
          onMouseEnter={() => {
            if (hideTimer.current) clearTimeout(hideTimer.current);
            setShowActions(true);
          }}
          onMouseLeave={handleMouseLeave}
        >
          <DeleteButton
            visible={true}
            onDelete={handleDelete}
            isEdge={true}
            onEnableDownstream={handleEnableDownstream}
            onDisableDownstream={handleDisableDownstream}
            onSwapDirection={handleSwapDirection}
          />
        </div>
      )}
    </EdgeLabelRenderer>
  </>
  );
};

export default CustomEdge;
